if (!window.HeroBannerInitialized) {
  window.HeroBannerInitialized = true;

  class HeroBanner {
    static instances = new Map();

    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      if (!(root instanceof HTMLElement)) return;

      const existing = HeroBanner.instances.get(root.id);
      if (existing) existing.destroy();

      this.root = root;
      this.swiperEl = root.querySelector('[data-hero-swiper]');
      this.paginationEl = root.querySelector('[data-hero-pagination]');
      this.swiper = null;

      const controlsType = root.dataset.controlsType || 'dots';

      this.settings = {
        autoplay: root.dataset.autoplay === 'true',
        autoplaySpeed: Number(root.dataset.autoplaySpeed) || 5,
        speed: Number(root.dataset.speed) || 400,
        pauseOnHover: root.dataset.pauseOnHover !== 'false',
        controlsType,
        showArrows: controlsType === 'filled_arrows' || controlsType === 'outline_arrows',
        showNumbers: controlsType === 'numbers',
        showDots: controlsType === 'dots',
        showProgress: Boolean(this.paginationEl?.hasAttribute('data-hero-progress')),
      };

      this._readyPromise = this._whenSwiperReady()
        .then(() => this.init())
        .catch(() => {});

      HeroBanner.instances.set(root.id, this);
    }

    get autoplayConfig() {
      if (!this.settings.autoplay) return false;

      return {
        delay: this.settings.autoplaySpeed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: this.settings.pauseOnHover,
      };
    }

    /**
     * @param {HTMLElement} swiperEl
     * @returns {false | { prevEl: Element, nextEl: Element }}
     */
    _navigationConfig(swiperEl) {
      if (!this.settings.showArrows) return false;

      const wrapper = swiperEl.closest('.hero-banner');
      if (!wrapper) return false;

      const prevEl = wrapper.querySelector('[data-hero-prev]');
      const nextEl = wrapper.querySelector('[data-hero-next]');

      if (!prevEl || !nextEl) return false;

      return { prevEl, nextEl };
    }

    /**
     * Numbered progress rings or stretching dots (slideshow-style).
     * @returns {false | object}
     */
    _paginationConfig() {
      if ((!this.settings.showNumbers && !this.settings.showDots) || !this.paginationEl) {
        return false;
      }

      if (this.settings.showDots) {
        return {
          el: this.paginationEl,
          clickable: true,
          renderBullet(index, className) {
            const label = index + 1;
            return (
              `<button type="button" class="${className} tap-area" aria-current="false">` +
              `<span class="sr-only">Slide ${label}</span>` +
              `</button>`
            );
          },
        };
      }

      const showProgress = this.settings.showProgress;

      return {
        el: this.paginationEl,
        clickable: true,
        renderBullet(index, className) {
          const label = index + 1;
          const progressAttr = showProgress ? ' style="--progress: 0"' : '';

          return (
            `<button type="button" class="${className} numbered-dots__item" aria-label="${label}" aria-current="false">` +
            `<svg width="100%" height="100%"${progressAttr} aria-hidden="true">` +
            `<circle cx="50%" cy="50%" r="15"></circle>` +
            `<circle cx="50%" cy="50%" r="15"></circle>` +
            `</svg>` +
            `<span>${label}</span>` +
            `</button>`
          );
        },
      };
    }

    /**
     * Sync aria-current and reset progress rings on slide change.
     */
    _syncPaginationState() {
      if (!this.paginationEl) return;

      const bullets = this.paginationEl.querySelectorAll('.swiper-pagination-bullet');

      bullets.forEach((bullet) => {
        const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
        bullet.setAttribute('aria-current', isActive ? 'true' : 'false');

        if (!this.settings.showNumbers || !this.settings.showProgress) return;

        const svg = bullet.querySelector('svg');
        if (svg) {
          svg.style.setProperty('--progress', '0');
        }
      });
    }

    /**
     * @param {number} progressRatio 1 = start of delay, 0 = end
     */
    _updateProgress(progressRatio) {
      if (!this.paginationEl || !this.settings.showProgress || !this.settings.showNumbers) return;

      const filled = Math.max(0, Math.min(1, 1 - progressRatio));

      this.paginationEl.querySelectorAll('.numbered-dots__item').forEach((bullet) => {
        const svg = bullet.querySelector('svg');
        if (!svg) return;

        const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
        svg.style.setProperty('--progress', isActive ? String(filled) : '0');
      });
    }

    /**
     * @returns {Promise<void>}
     */
    _whenSwiperReady() {
      if (typeof window.Swiper !== 'undefined') {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const started = Date.now();
        const timer = window.setInterval(() => {
          if (typeof window.Swiper !== 'undefined') {
            window.clearInterval(timer);
            resolve();
          } else if (Date.now() - started > 8000) {
            window.clearInterval(timer);
            reject(new Error('[HeroBanner] Swiper failed to load'));
          }
        }, 50);
      });
    }

    init() {
      if (typeof window.Swiper === 'undefined' || !this.swiperEl) return;

      const slides = this.swiperEl.querySelectorAll('.swiper-slide');
      const slideCount = slides.length;

      if (slideCount === 0) return;

      const navigation = this._navigationConfig(this.swiperEl);
      const pagination = this._paginationConfig();
      const inDesignMode = Boolean(window.Shopify?.designMode);
      const enableLoop = slideCount > 1 && !inDesignMode;
      const enableAutoplay = slideCount > 1 && Boolean(this.autoplayConfig) && !inDesignMode;

      this.swiper = new window.Swiper(this.swiperEl, {
        slidesPerView: 1,
        loop: enableLoop,
        speed: this.settings.speed,
        autoplay: enableAutoplay ? this.autoplayConfig : false,
        allowTouchMove: slideCount > 1,
        watchOverflow: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        ...(navigation ? { navigation } : {}),
        ...(pagination ? { pagination } : {}),
        on: {
          init: () => {
            this._syncPaginationState();
            if (!enableAutoplay && this.settings.showProgress) {
              const active = this.paginationEl?.querySelector(
                '.swiper-pagination-bullet-active svg'
              );
              if (active) active.style.setProperty('--progress', '1');
            }
          },
          slideChange: () => {
            this._syncPaginationState();
          },
          autoplayTimeLeft: (_swiper, _time, progress) => {
            this._updateProgress(progress);
          },
        },
      });
    }

    /**
     * @param {string} blockId
     * @param {{ animate?: boolean }} [options]
     */
    selectBlock(blockId, { animate = true } = {}) {
      if (!this.swiper || !blockId) return;

      // Prefer original slides (ignore Swiper loop clones when present).
      const slides = this.swiperEl.querySelectorAll(
        '.swiper-slide[data-block-id]:not(.swiper-slide-duplicate)'
      );
      let index = -1;

      slides.forEach((slide, i) => {
        if (slide.getAttribute('data-block-id') === blockId) {
          index = i;
        }
      });

      if (index < 0) return;

      const speed = animate ? this.settings.speed : 0;

      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index, speed);
      } else {
        this.swiper.slideTo(index, speed);
      }
    }

    pauseAutoplay() {
      this.swiper?.autoplay?.stop?.();
    }

    resumeAutoplay() {
      if (!this.settings.autoplay || window.Shopify?.designMode) return;
      this.swiper?.autoplay?.start?.();
    }

    destroy() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      HeroBanner.instances.delete(this.root?.id);
    }

    /**
     * @param {HTMLElement | null} root
     */
    static initSection(root) {
      if (!root) return;
      new HeroBanner(root);
    }

    /**
     * @param {HTMLElement | null} root
     */
    static destroySection(root) {
      if (!root) return;
      const instance = HeroBanner.instances.get(root.id);
      if (instance) instance.destroy();
    }

    /**
     * @param {EventTarget | null} target
     * @returns {HTMLElement | null}
     */
    static findRoot(target) {
      if (!(target instanceof HTMLElement)) return null;

      if (target.matches('[data-section-type="hero-banner"]')) {
        return target;
      }

      // block:select → slide is inside hero; section:load → shopify-section wraps hero
      return (
        target.closest('[data-section-type="hero-banner"]') ||
        target.querySelector('[data-section-type="hero-banner"]')
      );
    }
  }

  function bootHeroBanners() {
    document.querySelectorAll('[data-section-type="hero-banner"]').forEach((el) => {
      HeroBanner.initSection(el);
    });
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = HeroBanner.findRoot(event.target);
    if (section) HeroBanner.initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = HeroBanner.findRoot(event.target);
    if (section) HeroBanner.destroySection(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = HeroBanner.findRoot(event.target);
    if (!section) return;

    const instance = HeroBanner.instances.get(section.id);
    if (!instance) return;

    instance.pauseAutoplay();

    const slide =
      event.target instanceof HTMLElement
        ? event.target.closest('.swiper-slide[data-block-id]')
        : null;
    const blockId = slide?.getAttribute('data-block-id') || event.detail?.blockId;

    if (blockId) {
      instance.selectBlock(blockId, { animate: !event.detail?.load });
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const section = HeroBanner.findRoot(event.target);
    if (!section) return;

    const instance = HeroBanner.instances.get(section.id);
    instance?.resumeAutoplay();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootHeroBanners);
  } else {
    bootHeroBanners();
  }

  window.HeroBanner = HeroBanner;
}
